// inventoZen-Back-End/front/src/components/form-fields/TableFileField.tsx
import {
  useDeleteMultipleSample,
  useGetAllMultipleSamples,
} from "@/api/multiplesample";
import { toast } from "@/hooks/use-toast";
import { Trash2 } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import EditExemplaireModal from "../modal/EditExemplaireModal";
import { ImageCell } from "../table/CustomCell";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { FormFieldProps } from "./types";

interface TableExemplaireProps extends Pick<FormFieldProps, "config"> {
  idMultipleprint: string;
}

const TableExemplaire: React.FC<TableExemplaireProps> = ({
  idMultipleprint,
  config,
}) => {
  const { t } = useTranslation();
  const { data } = useGetAllMultipleSamples({
    where: {
      multipleprints: {
        some: {
          id: idMultipleprint,
        },
      },
    },
    order: {
      createdAt: "desc",
    },
    take: 100,
    skip: 0,
  });

  const { mutate: deleteMultipleSample } = useDeleteMultipleSample();
  const actions = config.table?.actions;

  const handleDeleteMultipleSample = async (id: string) => {
    try {
      await deleteMultipleSample(id);
      toast({
        title: t("successDeleteExemplaire"),
        variant: "success",
      });
    } catch (error) {
      toast({
        title: t("errorDeleteExemplaire"),
        variant: "destructive",
      });
      console.error(error);
    }
  };

  const columnsData = React.useMemo(
    () => [
      ...(config.table?.columns.map((column) => {
        if (column.id === "attachment") {
          return {
            ...column,
            cell: ({ row }: any) => {
              const attachment = row.original.attachments;
              if (attachment.length > 0) {
                return (
                  <ImageCell
                    value={attachment[0].url}
                    attachments={attachment}
                    domain="multiplesample"
                  />
                );
              }
              return null;
            },
          };
        }
        return column;
      }) ?? []),
      {
        header: "Actions",
        size: 10,
        cell: ({ row }: any) => (
          <div className="flex space-x-2">
            {actions?.map((action) => {
              switch (action.name) {
                case "show":
                  return (
                    <ShowElements
                      type="multiplesample"
                      elements={[
                        {
                          label: t(
                            "exemplaireDansEstampesMultiplesDescriptionNumero",
                          ),
                          value:
                            row.original
                              .exemplaireDansEstampesMultiplesDescriptionNumero,
                          type: "text",
                        },
                        {
                          label: t(
                            "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
                          ),
                          value:
                            row.original
                              .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire,
                          type: "text",
                        },
                        {
                          label: t(
                            "exemplaireDansEstampesMultiplesDescriptionLocalisation",
                          ),
                          value:
                            row.original
                              .exemplaireDansEstampesMultiplesDescriptionLocalisation,
                          type: "text",
                        },
                        {
                          label: t(
                            "exemplaireDansEstampesMultiplesDescriptionNotes",
                          ),
                          value:
                            row.original
                              .exemplaireDansEstampesMultiplesDescriptionNotes,
                          type: "richtext",
                        },
                        {
                          label: t("attachments"),
                          value: row.original.attachments,
                          type: "image",
                        },
                      ]}
                    />
                  );
                case "delete":
                  return (
                    <Button
                      key="delete"
                      size="icon"
                      type="button"
                      variant="ghost"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDeleteMultipleSample(row.original.id);
                      }}
                    >
                      <Trash2 className="w-5 h-5 text-gray-500" />
                    </Button>
                  );
                case "edit":
                  return <EditExemplaireModal multiplesample={row.original} />;
                default:
                  return null;
              }
            })}
          </div>
        ),
      },
    ],
    [config],
  );

  return (
    <DataTable
      className={config.className}
      columns={columnsData}
      data={data ?? []}
      options={{}}
    />
  );
};

export default TableExemplaire;
